import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { rolesApi } from '../../../api/roles.js';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { useMfaStepUp } from '../../../hooks/useMfaStepUp.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { DataTable } from '../../../components/DataTable.jsx';
import { Pagination } from '../../../components/ui/Pagination.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { MfaPrompt } from '../../../components/MfaPrompt.jsx';
import { t } from '../../../lib/i18n.js';

import { RoleFormModal } from './RoleFormModal.jsx';

export function RolesPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const { run, mfaProps } = useMfaStepUp();

  const canCreate = hasPermission('roles:create');
  const canUpdate = hasPermission('roles:update');
  const canDelete = hasPermission('roles:delete');

  const { items, isLoading, pagination, page, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['roles', 'list'],
    fetcher: rolesApi.list
  });

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: (id) => run(() => rolesApi.remove(id)),
    onSuccess: () => {
      toast.success('Ruolo eliminato.');
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setConfirmTarget(null);
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const columns = [
    { key: 'name', header: 'Slug', render: (r) => <code className="rounded bg-border/40 px-1.5 py-0.5 text-xs">{r.name}</code> },
    { key: 'label', header: 'Etichetta' },
    {
      key: 'permissions',
      header: 'Permessi',
      render: (r) => <span className="text-xs text-ink-muted">{(r.permissions && r.permissions.length) || 0}</span>
    },
    {
      key: 'isSystem',
      header: 'Tipo',
      render: (r) => r.isSystem ? <Badge tone="info">Sistema</Badge> : <Badge>Custom</Badge>
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-2">
          {canUpdate && (
            <button
              type="button"
              className="text-xs text-ink-muted hover:text-ink"
              onClick={() => { setEditing(r); setFormOpen(true); }}
            >
              Modifica
            </button>
          )}
          {canDelete && !r.isSystem && (
            <button
              type="button"
              className="text-xs text-danger hover:underline"
              onClick={() => setConfirmTarget(r)}
            >
              Elimina
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('nav.roles')}
        description="Ruoli applicativi e relativi permessi. I ruoli di sistema non possono essere eliminati."
        actions={canCreate ? <Button onClick={() => { setEditing(null); setFormOpen(true); }}>Nuovo ruolo</Button> : null}
      />

      <div>
        <Input
          className="w-64"
          label="Cerca"
          placeholder="slug o etichetta..."
          value={filters.q || ''}
          onChange={(e) => setFilters({ q: e.target.value || undefined })}
        />
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        empty={{ title: 'Nessun ruolo', description: 'Esegui `pnpm rbac:seed` per popolare i ruoli base.' }}
      />

      <Pagination pagination={pagination} onPageChange={setPage} />

      <RoleFormModal
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        initial={editing}
      />

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title="Eliminare il ruolo?"
        description={confirmTarget ? `Il ruolo "${confirmTarget.label}" sarà rimosso. Servirà una verifica MFA recente.` : ''}
        destructive
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => deleteMutation.mutate(confirmTarget._id)}
      />

      <MfaPrompt {...mfaProps} />
    </div>
  );
}
